import type { ReactNode } from "react"
import { Check } from "lucide-react"

import { cn } from "@anysola/ui/utils"

/*
 * The pieces of a plan card, shared by /pricing and the dashboard's upgrade
 * list so a plan looks the same wherever it is offered.
 */

function FeatureList({ items }: { items: string[] }) {
  if (items.length === 0) return null
  return (
    <ul className="mt-6 grid gap-2.5 text-sm">
      {items.map((item) => (
        <li key={item} className="flex gap-2.5 text-foreground">
          <Check
            className="mt-0.5 size-4 shrink-0 text-muted-foreground"
            aria-hidden
          />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  )
}

/** The card frame: every plan on the same card tone; Pro stands out by
 *  its size, not a lighter fill. */
function PlanFrame({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return (
    <div className={cn("flex flex-col rounded-surface bg-card p-6", className)}>
      {children}
    </div>
  )
}

/** Name, an optional badge, then the price large and light — larger still
 *  on the featured plan. */
function PlanHead({
  name,
  badge,
  price,
  period,
  note,
  featured = false,
}: {
  name: string
  badge?: ReactNode
  price: string
  period: string
  note?: string | null
  featured?: boolean
}) {
  return (
    <>
      <div className="flex min-h-6 items-center justify-between gap-2">
        <h2 className="text-xl font-medium tracking-tight text-foreground">
          {name}
        </h2>
        {badge}
      </div>
      <p className="mt-5 flex items-baseline gap-1.5">
        <span
          className={cn(
            "leading-none font-light tracking-[-0.04em] text-foreground tabular-nums",
            featured ? "text-6xl" : "text-5xl"
          )}
        >
          {price}
        </span>
        <span className="text-sm text-muted-foreground">{period}</span>
      </p>
      <p className="mt-2 h-4 text-xs text-muted-foreground">{note}</p>
    </>
  )
}

export { FeatureList, PlanFrame, PlanHead }
